import type {
  Equality,
  Site,
  SiteKind,
  SiteUUID,
  BodyUUID,
  Quaternion,
  Vector3,
} from "../../types/scene";
import { create } from "mutative";
import { generateUuid } from "../../utils/id";
import type { SceneStoreSetState, SceneStoreGetState } from "../sceneStore.types";
import type { SiteActions, SiteInput, SiteMap } from "./actionTypes";
import { withHistory } from "./withHistory";
import { cleanupReferencesForDeletedTargets } from "./referenceCleanup";
import { logger } from "../../utils/logger";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";

type BodyWithSites = { sites?: SiteUUID[] };
type BodyMapWithSites = Record<string, BodyWithSites>;
type BodiesWithSites = BodyMapWithSites | BodyWithSites[];
type SiteEquality = Equality & { name1?: string; name2?: string };
type SiteEqualityMap = Record<string, SiteEquality>;

const ensureSiteUuid = (site: SiteInput): Site => {
  if (site.uuid) return site as Site;
  return { ...site, uuid: generateUuid() as SiteUUID };
};

const ensureSiteName = (site: Site, fallbackName: string): Site => {
  if (site.name) return site;
  return { ...site, name: fallbackName };
};

const normalizeSiteMap = (siteMap: Record<string, SiteInput> = {}): SiteMap => {
  const result: SiteMap = {};
  Object.entries(siteMap).forEach(([key, site]) => {
    if (!site) return;
    const withUuid = ensureSiteUuid(site);
    const finalized = ensureSiteName(withUuid, site.name ?? key);
    result[finalized.uuid] = finalized;
  });
  return result;
};

const findSiteInMap = (siteMap: SiteMap, siteIdentifier: string): Site | null => {
  if (!siteIdentifier) return null;
  if (siteMap[siteIdentifier]) return siteMap[siteIdentifier];
  return Object.values(siteMap).find((site) => site?.name === siteIdentifier) || null;
};

const EDITABLE_SITE_KINDS = new Set<SiteKind>([
  "sphere",
  "box",
  "cylinder",
  "capsule",
  "ellipsoid",
]);

const sanitizeSiteKind = (kind: SiteKind): SiteKind => {
  return EDITABLE_SITE_KINDS.has(kind) ? kind : "sphere";
};

const sanitizeSiteSize = (size: Vector3): Vector3 => {
  const source = Array.isArray(size) ? size : [];
  return [0, 1, 2].map((index) => {
    const value = Number(source[index]);
    return Number.isFinite(value) && value > 0 ? value : 0.02;
  }) as Vector3;
};

function resolveUniqueSiteName(
  sites: Record<string, Site | undefined> | undefined,
  rawInputName: string | undefined,
  newUuid: SiteUUID
): string {
  const siteValues = Object.values(sites || {}).filter(Boolean) as Site[];
  const rawName = typeof rawInputName === "string" ? rawInputName.trim() : "";
  if (!rawName) {
    let counter = 1;
    while (siteValues.some((s) => s.name === `site_${counter}`)) {
      counter += 1;
    }
    return `site_${counter}`;
  }
  let counter = 1;
  let tempName = rawName;
  while (siteValues.some((s) => s.name === tempName && s.uuid !== newUuid)) {
    tempName = `${rawName}_${counter}`;
    counter += 1;
  }
  return tempName;
}

/**
 * Validates and normalizes body.sites to ensure they're all valid UUIDs.
 * Works directly with flat bodies structure - no tree traversal needed.
 *
 * @param bodiesMap - Flat map of bodies by UUID
 * @param siteMap - Map of sites by UUID
 */
export const embedSitesIntoBodies = (bodiesMap: BodiesWithSites, siteMap: SiteMap = {}) => {
  if (!bodiesMap || typeof bodiesMap !== "object") return;

  const bodies = Array.isArray(bodiesMap) ? bodiesMap : Object.values(bodiesMap);

  bodies.forEach((body) => {
    if (!body) return;
    const entries: SiteUUID[] = Array.isArray(body.sites) ? body.sites : [];
    const normalizedEntries: SiteUUID[] = [];
    const existingUuids = new Set<SiteUUID>();

    entries.forEach((entry) => {
      const site = findSiteInMap(siteMap, entry);
      if (site && !existingUuids.has(site.uuid)) {
        existingUuids.add(site.uuid);
        normalizedEntries.push(site.uuid);
      } else if (!site && typeof entry === "string" && !existingUuids.has(entry as SiteUUID)) {
        // Keep unknown UUIDs so we don't drop references that might resolve later
        existingUuids.add(entry as SiteUUID);
        normalizedEntries.push(entry as SiteUUID);
      }
    });

    body.sites = normalizedEntries;
  });
};

export const createSiteActions = (
  set: SceneStoreSetState,
  _get: SceneStoreGetState
): SiteActions => ({
  setSites: (sitesInput: SiteInput[] | Record<string, SiteInput>) => {
    if (shouldBlockSceneAuthoringMutation("setSites")) return;
    const siteMap: SiteMap = Array.isArray(sitesInput)
      ? sitesInput.reduce((acc, site, index) => {
          if (!site) return acc;
          const withUuid = ensureSiteUuid(site);
          const finalized = ensureSiteName(withUuid, site.name ?? `site_${index + 1}`);
          acc[finalized.uuid] = finalized;
          return acc;
        }, {} as SiteMap)
      : normalizeSiteMap(sitesInput || {});

    set((state) =>
      create(state, (draft) => {
        draft.sites = siteMap;
        // Direct access to flat bodies map - no tree reconstruction needed
        embedSitesIntoBodies(draft.bodies, siteMap);
      })
    );
  },

  moveSiteToBody: withHistory((siteUuid: SiteUUID, targetBodyUuid: BodyUUID) =>
    set((state) =>
      create(state, (draft) => {
        // Find current parent body by searching for siteUuid in body.sites arrays
        let currentParentBody: BodyWithSites | null = null;
        let siteIndex = -1;

        for (const body of Object.values(draft.bodies as BodyMapWithSites)) {
          if (Array.isArray(body?.sites)) {
            const idx = body.sites.indexOf(siteUuid);
            if (idx !== -1) {
              currentParentBody = body;
              siteIndex = idx;
              break;
            }
          }
        }

        if (!currentParentBody || siteIndex === -1) {
          const targetBody = draft.bodies[targetBodyUuid] || null;
          if (!targetBody) return;
          targetBody.sites = Array.isArray(targetBody.sites) ? targetBody.sites : [];
          if (!targetBody.sites.includes(siteUuid)) {
            targetBody.sites.push(siteUuid);
          }
          return;
        }

        const targetBody = draft.bodies[targetBodyUuid] || null;
        if (!targetBody) return;

        // Remove from current parent
        if (Array.isArray(currentParentBody.sites)) {
          currentParentBody.sites.splice(siteIndex, 1);
        }

        // Add to new parent
        targetBody.sites = Array.isArray(targetBody.sites) ? targetBody.sites : [];
        targetBody.sites.push(siteUuid);
      })
    )
  ),

  setSitePosition: withHistory((siteUuid: SiteUUID, position: Vector3) => {
    const toVec3 = (v: Vector3): Vector3 => {
      const x = Number(v?.[0]);
      const y = Number(v?.[1]);
      const z = Number(v?.[2]);
      return [Number.isFinite(x) ? x : 0, Number.isFinite(y) ? y : 0, Number.isFinite(z) ? z : 0];
    };
    set((state) =>
      create(state, (draft) => {
        const site = draft.sites?.[siteUuid] || null;
        if (!site) return;
        site.position = toVec3(position);
      })
    );
  }),

  setSiteName: withHistory((siteUuid: SiteUUID, name: string) => {
    set((state) =>
      create(state, (draft) => {
        const site = draft.sites?.[siteUuid] || null;
        if (!site) return;
        site.name = name;
      })
    );
  }),

  setSiteOrientation: withHistory((siteUuid: SiteUUID, quaternion: Quaternion) => {
    const normalize = (q: Quaternion): Quaternion => {
      const w = Number(q?.[0]);
      const x = Number(q?.[1]);
      const y = Number(q?.[2]);
      const z = Number(q?.[3]);
      if (![w, x, y, z].every(Number.isFinite)) return [1, 0, 0, 0];
      const len = Math.hypot(w, x, y, z);
      if (len < 1e-8) return [1, 0, 0, 0];
      return [w / len, x / len, y / len, z / len];
    };

    set((state) =>
      create(state, (draft) => {
        const site = draft.sites?.[siteUuid] || null;
        if (!site) return;
        site.orientation = normalize(quaternion);
      })
    );
  }),

  setSiteKindAndSize: withHistory((siteUuid: SiteUUID, kind: SiteKind, size: Vector3) => {
    const nextKind = sanitizeSiteKind(kind);
    const nextSize = sanitizeSiteSize(size);
    set((state) =>
      create(state, (draft) => {
        const site = draft.sites?.[siteUuid] || null;
        if (!site) return;
        site.kind = nextKind;
        site.size = nextSize;
      })
    );
  }),

  addSite: withHistory((siteInput: SiteInput, parentBodyUuid: BodyUUID | null): string => {
    const withUuid = ensureSiteUuid(siteInput);
    let addedUuid = withUuid.uuid;

    set((state) =>
      create(state, (draft) => {
        const finalName = resolveUniqueSiteName(draft.sites, siteInput.name, withUuid.uuid);
        const finalized: Site = {
          ...(withUuid as Site),
          ...siteInput,
          uuid: withUuid.uuid,
          name: finalName,
        };
        draft.sites[finalized.uuid] = finalized;
        addedUuid = finalized.uuid;

        if (parentBodyUuid) {
          const parentBody = draft.bodies[parentBodyUuid] || null;
          if (parentBody) {
            parentBody.sites = parentBody.sites || [];
            parentBody.sites.push(finalized.uuid);
          } else {
            logger.warn(`[addSite] Parent body ${parentBodyUuid} not found`);
          }
        }
      })
    );

    // The viewer's rebuild signature tracks bodies and joints but not sites,
    // so nudge the Scene to rebuild when a new site is added.
    try {
      window.dispatchEvent(new CustomEvent("structure-changed"));
    } catch (error) {
      logger.warn("[siteActions] Failed to dispatch structure-changed after addSite", error);
    }
    return addedUuid;
  }),

  removeSite: withHistory((siteUuid: SiteUUID) => {
    set((state) =>
      create(state, (draft) => {
        if (!siteUuid) return;

        const removedSiteName =
          typeof draft.sites?.[siteUuid]?.name === "string" ? draft.sites[siteUuid].name : null;

        delete draft.sites[siteUuid];

        Object.values(draft.bodies as BodyMapWithSites).forEach((body) => {
          if (!Array.isArray(body?.sites)) return;
          body.sites = body.sites.filter((uuid: string) => uuid !== siteUuid);
        });

        // Drop site-based equality constraints that referenced this site.
        // We match on both UUID and (when we recorded it) the site name, because
        // some legacy equalities store the name instead of the UUID in obj1/obj2.
        const equalities = draft.equality as SiteEqualityMap;
        Object.entries(equalities).forEach(([eqUuid, eq]) => {
          if (!eq || eq.objtype !== "site") return;
          const obj1 = typeof eq.obj1 === "string" ? eq.obj1 : null;
          const obj2 = typeof eq.obj2 === "string" ? eq.obj2 : null;
          const name1 = typeof eq.name1 === "string" ? eq.name1 : null;
          const name2 = typeof eq.name2 === "string" ? eq.name2 : null;
          const matches = (value: string | null) =>
            !!value &&
            (value === siteUuid || (removedSiteName !== null && value === removedSiteName));
          if (matches(obj1) || matches(obj2) || matches(name1) || matches(name2)) {
            delete equalities[eqUuid];
          }
        });

        cleanupReferencesForDeletedTargets({
          draft,
          deletedSiteUuids: new Set<string>([siteUuid]),
          cameras: { mode: "downgrade" },
        });
      })
    );

    try {
      window.dispatchEvent(new CustomEvent("structure-changed"));
    } catch (error) {
      logger.warn("[siteActions] Failed to dispatch structure-changed after removeSite", error);
    }
  }),
});
